import { alpha, lighten } from '@mui/material/styles';
import type { LandingTokens, SharedStyles, Sx } from '../types';
import { BREAKPOINTS, down } from './breakpoints';

export const wrapSx = (): Sx => ({
  width: 'min(1180px, calc(100% - 48px))',
  margin: '0 auto',
  [down(BREAKPOINTS.mobile)]: { width: 'calc(100% - 40px)' },
});

export const sectionHeadSx = (): Sx => ({
  textAlign: 'center',
  maxWidth: 640,
  margin: '0 auto 22px',
});

const buttonBase = (t: LandingTokens): Sx => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  fontFamily: t.sans,
  fontWeight: 600,
  fontSize: 14,
  lineHeight: 1,
  padding: '12px 20px',
  borderRadius: 999,
  border: '1px solid transparent',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  transition:
    'transform .18s ease, box-shadow .18s ease, background .18s ease, color .18s ease, border-color .18s ease',
  [down(BREAKPOINTS.mobile)]: { padding: '10px 14px', fontSize: 13 },
  '&[aria-busy="true"]': { cursor: 'progress' },
  // `:hover` too, so the variants' lift and shadow stay off.
  '&:disabled, &:disabled:hover': {
    cursor: 'not-allowed',
    opacity: 0.55,
    transform: 'none',
    boxShadow: 'none',
  },
});

export const sharedStyles = (t: LandingTokens): SharedStyles => ({
  page: {
    containerType: 'inline-size',
    width: '100%',
    backgroundColor: t.paper,
    color: t.ink,
    fontFamily: t.sans,
    lineHeight: 1.55,
    WebkitFontSmoothing: 'antialiased',
    overflowX: 'hidden',
    '& img, & svg': { display: 'block', maxWidth: '100%' },
    // Underline only. A colour here would emit `.page a` (0,1,1) and beat a button's own
    // class (0,1,0), so an anchor CTA would ignore its variant. Each link sets its own colour.
    '& a': { textDecoration: 'none' },
  },
  wrap: wrapSx(),
  sectionHead: sectionHeadSx(),
  sectionTitle: {
    fontFamily: t.serif,
    fontWeight: 400,
    fontSize: 'clamp(32px, 4cqi, 46px)',
    lineHeight: 1.12,
    letterSpacing: '-.01em',
    margin: 0,
  },
  sectionLede: { marginTop: '14px', marginBottom: 0, color: t.muted, fontSize: '16.5px' },
  kicker: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '.22em',
    textTransform: 'uppercase',
    color: t.brandInk,
    marginBottom: '18px',
  },
  kickerIcon: { width: 14, height: 14, color: t.brand },
  accent: {
    light: { fontStyle: 'italic', color: t.brandInk },
    dark: { fontStyle: 'italic', fontWeight: 420, color: lighten(t.brand, 0.28) },
  },
  button: {
    brand: {
      ...buttonBase(t),
      backgroundColor: t.brand,
      color: '#fff',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: `0 12px 26px -10px ${alpha(t.brand, 0.65)}`,
      },
    },
    ghost: {
      ...buttonBase(t),
      borderColor: t.line,
      color: t.ink,
      backgroundColor: 'transparent',
      '&:hover': { borderColor: t.ink },
    },
    light: {
      ...buttonBase(t),
      backgroundColor: t.paperHi,
      color: t.ink,
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 12px 26px -14px rgba(0,0,0,.6)',
      },
    },
    dark: {
      ...buttonBase(t),
      borderColor: 'rgba(255,255,255,.25)',
      color: t.onDark,
      backgroundColor: 'transparent',
      '&:hover': { borderColor: t.onDark },
    },
  },
  buttonIcon: { width: 15, height: 15 },
});
